import { IContextualMenuItem } from 'office-ui-fabric-react/lib/ContextualMenu';
import { assertNever } from 'owa-assert';
import { ComposeUniqueState, IAttachmentLocalComputerFilePicker } from 'owa-attachment-filepicker';
import {
    AttachmentMenuInfo,
    attachmentMultiMenuEntry,
    AttachmentMultiMenuEntry,
} from 'owa-attachment-multi-menu-actions';
import {
    getMRUAttachmentStore,
    lazyLogAttachmentMenuSuggestionData,
} from 'owa-attachment-suggestions';
import getComposeUniqueState from 'owa-mail-compose-actions/lib/utils/getComposeUniqueState';
import { ComposeViewState } from 'owa-mail-compose-store';
import {
    AttachmentSuggestions,
    ClientItem,
    mailStore,
    ReplyWithAttachmentSuggestion,
    ReplyWithEndpointType,
} from 'owa-mail-store';
import ItemId from 'owa-service/lib/contract/ItemId';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import isSmimeEnabledInViewState from 'owa-smime/lib/utils/isSmimeEnabledInViewState';
import { logUserAction, SurfaceActionItem } from 'owa-surface-actions';
import * as React from 'react';
import AttachmentMenuEntrySource from '../types/AttachmentMenuEntrySource';
import { buildReplyWithContextualMenuItems } from '../utils/attachButtonMenuItemBuilder';
import { openCloudFilePicker, openLocalFilePicker } from './openFilePicker';

export enum AttachButton {
    COMPUTER_ATTACHMENT_KEY = 'addMyComputerAttachment',
    ONLINE_LOCATION_KEY = 'addOnlineLocationAttachment',
    UPLOAD_AND_SHARE_FROM_CLOUD_KEY = 'addUploadAndShareFromCloud',
}

type AttachmentMultiMenuMap<T> = { [K in keyof AttachmentMultiMenuEntry]: T };

/**
 * To add a button, update the AttachButton enum, update getDefaultButtonsList, then update this method.
 */
export default function getAttachmentCommandItems(
    getAttachmentLocalComputerFilePicker: () => IAttachmentLocalComputerFilePicker | null,
    getAttachmentLocalComputerToCloudFilePicker: () => IAttachmentLocalComputerFilePicker | null,
    viewState: ComposeViewState,
    suggestedAttachmentsLogicalId: string,
    suggestedAttachmentsTraceId: string,
    clearSuggestedAttachmentsTraceId: () => void,
    attachMenuOpenTime: number,
    targetWindow: Window
): IContextualMenuItem[] {
    const attachmentMultiMenuInfo: AttachmentMultiMenuMap<{
        actionName: string;
        onClick: () => void;
    }> = {
        computer: {
            actionName: 'computerAttach',
            onClick: () =>
                openLocalFilePicker(
                    viewState,
                    getAttachmentLocalComputerFilePicker,
                    'AttachLocalComputerClicked'
                ),
        },
        onlineLocation: {
            actionName: 'onlineAttach',
            onClick: () => {
                openCloudFilePicker(viewState, targetWindow);
            },
        },
        uploadAndShareFromCloud: {
            actionName: 'computerToCloudAttach',
            onClick: () =>
                openLocalFilePicker(
                    viewState,
                    getAttachmentLocalComputerToCloudFilePicker,
                    'AttachUploadAndSharedClicked'
                ),
        },
    };

    const referenceAttachmentsSupported =
        getComposeUniqueState(viewState) === ComposeUniqueState.None;
    const isSmimeCompose = isSmimeEnabledInViewState(viewState.smimeViewState);
    const defaultButtonTypes: AttachButton[] = isSmimeCompose
        ? getSmimeAttachButtonsList()
        : getDefaultButtonsList(referenceAttachmentsSupported);

    const defaultButtons: IContextualMenuItem[] = defaultButtonTypes.map<IContextualMenuItem>(
        defaultButtonType => {
            switch (defaultButtonType) {
                case AttachButton.COMPUTER_ATTACHMENT_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.computer,
                        AttachButton.COMPUTER_ATTACHMENT_KEY,
                        attachmentMultiMenuInfo.computer.actionName,
                        attachmentMultiMenuInfo.computer.onClick
                    );
                case AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.uploadAndShareFromCloud,
                        AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY,
                        attachmentMultiMenuInfo.uploadAndShareFromCloud.actionName,
                        attachmentMultiMenuInfo.uploadAndShareFromCloud.onClick
                    );
                case AttachButton.ONLINE_LOCATION_KEY:
                    return createAttachmentContextMenuItem(
                        attachmentMultiMenuEntry.onlineLocation,
                        AttachButton.ONLINE_LOCATION_KEY,
                        attachmentMultiMenuInfo.onlineLocation.actionName,
                        attachmentMultiMenuInfo.onlineLocation.onClick
                    );
                default:
                    return assertNever(defaultButtonType);
            }
        }
    );

    // Attachment suggestions (replyWith + MRU)
    const referenceItem: ClientItem = mailStore.items.get(viewState.referenceItemId?.Id);
    const replyWithSuggestions: AttachmentSuggestions = referenceItem?.AttachmentSuggestions;
    if (replyWithSuggestions?.sentence) {
        replyWithSuggestions.sentence = replyWithSuggestions.sentence.filter(suggestion =>
            filterSuggestions(suggestion, referenceAttachmentsSupported)
        );
    }
    const combinedAttachmentSuggestions = getCombinedAttachmentSuggestions(
        viewState.referenceItemId
    );

    if (suggestedAttachmentsTraceId) {
        lazyLogAttachmentMenuSuggestionData.importAndExecute(
            combinedAttachmentSuggestions,
            suggestedAttachmentsLogicalId,
            suggestedAttachmentsTraceId,
            clearSuggestedAttachmentsTraceId,
            attachMenuOpenTime,
            viewState.referenceItemId
        );
    }

    if (!isSmimeCompose && (!!replyWithSuggestions || !!combinedAttachmentSuggestions?.length)) {
        return buildReplyWithContextualMenuItems(
            viewState,
            replyWithSuggestions,
            combinedAttachmentSuggestions.filter(suggestion =>
                filterSuggestions(suggestion, referenceAttachmentsSupported)
            )
        ).concat(defaultButtons);
    } else {
        return defaultButtons;
    }
}

export function filterSuggestions(
    suggestion: ReplyWithAttachmentSuggestion,
    referenceAttachmentsSupported: boolean
): boolean {
    if (!suggestion) {
        return false;
    }
    switch (suggestion.Endpoint) {
        case ReplyWithEndpointType.Contextual:
        case ReplyWithEndpointType.MRUCloudy:
        case ReplyWithEndpointType.MRUContextual:
            return referenceAttachmentsPolicyEnabled() && referenceAttachmentsSupported;
        case ReplyWithEndpointType.MRUClassic:
            return classicAttachmentsPolicyEnabled();
        default:
            return assertNever(suggestion.Endpoint);
    }
}

function createAttachmentContextMenuItem(
    menuInfo: AttachmentMenuInfo,
    attachButtonType: AttachButton,
    actionName: string,
    onClick: () => void
): IContextualMenuItem {
    const aria = menuInfo.ariaLabel?.();

    return {
        key: attachButtonType,
        name: menuInfo.text(),
        ariaLabel: aria,
        title: aria,
        iconProps: menuInfo.iconProps,
        onClick: (
            event: React.KeyboardEvent<HTMLElement> | React.MouseEvent<HTMLElement>,
            item?: SurfaceActionItem
        ) => {
            logUserAction(true /* isCompose */, actionName, item);
            menuInfo.logUsage(AttachmentMenuEntrySource.ToolBar);
            onClick();
        },
    };
}

/**
 * Returns a list of button names (email/cloud/local etc) shown in the Attach button.
 * Used for logging. Needs to be consistent with getAttachmentCommandItems method.
 */
export function getDefaultButtonsList(referenceAttachmentsSupported: boolean): AttachButton[] {
    const defaultButtons: AttachButton[] = [];
    const referenceAttachmentsEnabled =
        referenceAttachmentsPolicyEnabled() && referenceAttachmentsSupported;

    if (classicAttachmentsPolicyEnabled()) {
        defaultButtons.push(AttachButton.COMPUTER_ATTACHMENT_KEY);
    }
    if (classicAttachmentsPolicyEnabled() || referenceAttachmentsEnabled) {
        defaultButtons.push(AttachButton.ONLINE_LOCATION_KEY);
    }

    if (
        attachmentMultiMenuEntry.uploadAndShareFromCloud.isEnabled() &&
        referenceAttachmentsEnabled
    ) {
        defaultButtons.push(AttachButton.UPLOAD_AND_SHARE_FROM_CLOUD_KEY);
    }

    return defaultButtons;
}

/**
 * Returns the list of buttons that needed to be shown in attach menu
 * in case of a S/MIME compose.
 */
export function getSmimeAttachButtonsList(): AttachButton[] {
    return [AttachButton.COMPUTER_ATTACHMENT_KEY];
}

function classicAttachmentsPolicyEnabled(): boolean {
    const userConfiguration = getUserConfiguration();
    return userConfiguration.AttachmentPolicy.ClassicAttachmentsEnabled;
}

function referenceAttachmentsPolicyEnabled(): boolean {
    const userConfiguration = getUserConfiguration();
    return userConfiguration.AttachmentPolicy.ReferenceAttachmentsEnabled;
}

export function getCombinedAttachmentSuggestions(
    referenceItemId: ItemId | null
): ReplyWithAttachmentSuggestion[] {
    const referenceItem: ClientItem | null = mailStore.items.get(referenceItemId?.Id);
    const contextualAttachmentSuggestions = referenceItem?.ContextualAttachmentSuggestions;
    const mruSuggestions: ReplyWithAttachmentSuggestion[] | null = getMRUAttachmentStore()
        .attachments;

    return [...(contextualAttachmentSuggestions || []), ...(mruSuggestions || [])];
}

export function getAttachmentSuggestionsTraceId(referenceItemId: ItemId | null) {
    const suggestions = getCombinedAttachmentSuggestions(referenceItemId);
    const traceId = suggestions.length && suggestions[0]?.TraceId;
    if (traceId) {
        return traceId;
    }

    // if the suggestion list is empty,
    // the trace ID is also available in the item or the MRU attachment store
    const referenceItem: ClientItem | null = mailStore.items.get(referenceItemId?.Id);
    return referenceItem?.ContextualAttachmentSuggestionsTraceId || getMRUAttachmentStore().traceId;
}
